import React, { useContext, useState } from "react"
import { Col, Div, Row, Text } from "atomize"
import { toast } from "react-toastify"
import PrimaryButton from "../buttons/PrimaryButton"
import SecondaryButton from "../buttons/SecondaryButton"
import TextInput from "../inputs/TextInput"
import SegmentedPill from "../atoms/SegmentedPill"
import { SchemaThumbnail } from "../lesson/schema/ExerciseSchema"
import Paragraph from "../atoms/Paragraph"
import { LevelChips } from "./NotebookVariants"
import { theme } from "../settings/config"
import { profileContext } from "../providers/profile.provider"
import { addNotebookEntry } from "../utils/firestore.utils"
import { networkAwareMessage } from "../utils/network.utils"
import {
	NOTEBOOK_EXERCISE,
	NOTEBOOK_LESSON,
	NOTEBOOK_TITLE_MAX,
	phaseLabel,
} from "../utils/notebook.utils"

// Salvare nel quaderno dal foglio del programma: il programma intero come
// lezione, oppure un esercizio da solo, col suo schema se ce l'ha. Si chiede il nome e, per un
// esercizio, se vale solo per la sua fase o per qualsiasi.
//
// Nel quaderno va una copia: cambiare dopo la lezione non cambia il
// quaderno, e viceversa.
//
// Il livello parte da quello della lezione (il SAT o il PTR del CAD, se è
// segnato): è la sua prima variante, e si può cambiare o togliere.
function NotebookSaveForm({
	kind,
	phaseID,
	exerciseText = "",
	exerciseSchema = null,
	content,
	initialTitle = "",
	initialLevel = null,
	onSaved,
	onBack,
}) {
	const { coach } = useContext(profileContext)
	const [title, setTitle] = useState(initialTitle)
	const [level, setLevel] = useState(initialLevel)
	const [isGeneric, setGeneric] = useState(false)
	const [isSaving, setSavingState] = useState(false)

	const isExercise = kind === NOTEBOOK_EXERCISE
	const text = isExercise ? exerciseText.trim() : ""
	const canSave = title.trim() !== "" && Boolean(coach?.coachID) && !isSaving

	const save = async () => {
		if (!canSave) return
		setSavingState(true)
		const entry = isExercise
			? {
					kind: NOTEBOOK_EXERCISE,
					title,
					phase: isGeneric ? null : phaseID,
					level,
					text,
					schema: exerciseSchema,
				}
			: { kind: NOTEBOOK_LESSON, title, level, content }
		try {
			const { entryID, variantID } = await addNotebookEntry(coach.coachID, entry)
			onSaved({ ...entry, entryID, variantID, title: title.trim() })
		} catch (error) {
			const message = networkAwareMessage(error, "Non sono riuscito a salvarlo nel quaderno")
			toast.error(message, { toastId: message, position: "top-center" })
			setSavingState(false)
		}
	}

	return (
		<Div>
			<Text textSize="title" textWeight="bold" textColor={theme.colors.gunmetal}>
				{isExercise ? "Salva come esercizio" : "Salva nel quaderno"}
			</Text>
			<Text textColor={theme.colors.text_light} m={{ b: "0.75rem" }}>
				{isExercise
					? "Un esercizio di questa lezione, da riprendere in qualsiasi altra."
					: "Il programma di questa lezione, da riprendere quando vuoi. Cambiarlo dopo nel quaderno non cambia questa lezione."}
			</Text>

			<TextInput
				value={title}
				handleChange={e => setTitle(e.target.value.slice(0, NOTEBOOK_TITLE_MAX))}
				placeholder={isExercise ? "Nome dell'esercizio" : "Nome della lezione"}
				aria-label="Nome"
				submitAction={save}
			/>

			<Paragraph title="Per che livello:" m={{ t: "0.75rem", b: "0.5rem" }}>
				<LevelChips value={level} onChange={setLevel} disabled={isSaving} />
			</Paragraph>

			{isExercise && (
				<>
					<Div
						bg={theme.colors.surface}
						rounded="15px"
						p="0.75rem 1rem"
						m={{ y: "0.5rem" }}
						textSize="caption"
						d="flex"
						align="flex-start"
						style={{ whiteSpace: "pre-line", gap: "0.75rem" }}>
						<Div flexGrow="1" style={{ minWidth: 0 }}>
							{text}
						</Div>
						{exerciseSchema && (
							<SchemaThumbnail
								schema={exerciseSchema}
								width="4rem"
								height="4.5rem"
							/>
						)}
					</Div>
					<SegmentedPill
						label="Vale per"
						options={[
							{ value: "phase", text: phaseLabel(phaseID) },
							{ value: "generic", text: "Qualsiasi fase" },
						]}
						selected={isGeneric ? "generic" : "phase"}
						onChange={value => setGeneric(value === "generic")}
					/>
				</>
			)}

			<Div m={{ x: "1rem" }}>
				<Row m={{ t: "1rem" }}>
					<Col p="0">
						<SecondaryButton
							m="auto"
							textColor={theme.colors.gunmetal}
							text="Indietro"
							textSize="subheader"
							action={onBack}
						/>
					</Col>
					<Col p="0">
						<PrimaryButton
							m="auto"
							bg={theme.colors.primary}
							textColor="white"
							textSize="subheader"
							text={isSaving ? "Sto salvando..." : "Salva"}
							disabled={!canSave}
							action={save}
						/>
					</Col>
				</Row>
			</Div>
		</Div>
	)
}

export default NotebookSaveForm
